import { useEffect, useRef, useState } from 'react';
import type { AmenitySessionOut, AmenitySessionState } from '@/api/amenities';
import { formatBRL } from '@/lib/format';

const TERMINAL: AmenitySessionState[] = ['completed', 'failed'];

function fmtClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

interface Props {
  session: AmenitySessionOut;
  /** CTA de saída (ex.: "Voltar ao início"). Aparece nos estados terminais. */
  onExit?: () => void;
  exitLabel?: string;
}

/**
 * Miolo visual do cronômetro do aspirador — extraído de `VacuumActive.tsx`
 * (2026-07-08) pra reuso na página pública do Pix avulso (`VacuumLanding`).
 * O corte real é do hardware (`toggle_after`); aqui só contamos pra baixo a
 * partir de `expected_end_at`/`seconds_remaining` do snapshot mais recente.
 *
 * Estado ATIVO redesenhado no "Bow Carteiras" (T9Ligado): anel verde com a
 * fração restante do bloco + chip da carteira usada (`session.pay_from`).
 * SEM botão "Desligar" — ficou fora desta entrega (decisão do operador).
 * Estados failed/concluído mantêm a lógica e copy anteriores.
 */
export function VacuumCountdown({ session, onExit, exitLabel = 'Voltar ao início' }: Props) {
  const [now, setNow] = useState(Date.now());
  // Fallback relativo: só entra quando o backend não mandou `expected_end_at`.
  // Fixado uma vez pra não pular entre polls. `expected_end_at` já é absoluto,
  // então não precisa de ref nenhum.
  const fallbackTargetRef = useRef<number | null>(null);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  // Calculado DURANTE o render, não num efeito. Com o alvo vindo de efeito, no
  // primeiro frame ele era null → `countingDown` false → a tela caía em
  // "Encerrando…" toda vez que o componente montava (ao voltar do preparo e ao
  // reabrir o contador). E como gravar num ref não re-renderiza, aquilo ficava
  // na tela até o tick seguinte.
  if (!session.expected_end_at && fallbackTargetRef.current === null) {
    fallbackTargetRef.current = Date.now() + session.seconds_remaining * 1000;
  }
  const target = session.expected_end_at
    ? new Date(session.expected_end_at).getTime()
    : fallbackTargetRef.current;

  const state = session.state;
  const isTerminal = TERMINAL.includes(state);
  const remainingSec = target !== null ? (target - now) / 1000 : null;
  // "Encerrando…" só quando SABEMOS que o tempo acabou — o hardware corta um
  // instante depois do zero. Alvo desconhecido não é "acabando": cai no
  // cronômetro, que é o estado honesto enquanto não se sabe.
  const encerrando = state === 'active' && remainingSec !== null && remainingSec <= 0;

  if (state === 'failed') {
    return (
      <div className="relative flex flex-col items-center gap-3 text-center">
        <span className="text-5xl">⚠️</span>
        <p className="text-xl font-semibold">Não foi possível ligar</p>
        <p className="text-sm text-bow-cream/70">
          Nada foi cobrado. Tente novamente em instantes.
        </p>
        {onExit && <ExitButton onClick={onExit} label={exitLabel} />}
      </div>
    );
  }

  if (isTerminal || encerrando) {
    return (
      <div className="relative flex flex-col items-center gap-3 text-center">
        <span className="text-5xl">✨</span>
        <p className="text-xl font-semibold">
          {isTerminal ? 'Aspiração concluída' : 'Encerrando…'}
        </p>
        <p className="text-sm text-bow-cream/70">
          {isTerminal
            ? 'O aspirador desligou. Bom uso do carro limpo!'
            : 'O aspirador está desligando sozinho.'}
        </p>
        {isTerminal && onExit && <ExitButton onClick={onExit} label={exitLabel} />}
      </div>
    );
  }

  // Fração do anel = tempo restante / bloco total (T9Ligado).
  const fraction =
    session.block_seconds > 0 ? (remainingSec ?? 0) / session.block_seconds : 0;

  return (
    <div className="relative flex flex-col items-center gap-6 text-center">
      <span
        className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-bow-cream"
        style={{ background: 'rgba(241,237,228,0.14)' }}
      >
        <span
          className="h-2 w-2 animate-pulse rounded-full"
          style={{ background: '#00C072', boxShadow: '0 0 10px #00C072' }}
        />
        Aspirador ligado
      </span>

      <ProgressRing
        size={250}
        strokeWidth={10}
        fraction={fraction}
        color="#00C072"
        track="rgba(241,237,228,0.12)"
      >
        <span
          className="tabular-nums"
          style={{
            fontSize: 60,
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: '-0.02em'
          }}
        >
          {fmtClock(remainingSec ?? 0)}
        </span>
        <span className="mt-1 text-sm text-bow-cream/60">tempo restante</span>
      </ProgressRing>

      {/* Chip da carteira usada — só no fluxo logado (pay_from ausente no
          Pix avulso anônimo/legado). */}
      {session.pay_from && (
        <span
          className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-bow-cream"
          style={{
            borderRadius: 14,
            background: 'rgba(241,237,228,0.10)',
            border: '1px solid rgba(241,237,228,0.18)'
          }}
        >
          {session.pay_from === 'bonus' ? (
            <CashbackBadge size={24} />
          ) : (
            <WalletIcon size={18} />
          )}
          {session.pay_from === 'bonus' ? 'Cashback' : 'Saldo'} ·{' '}
          {formatBRL(session.price_brl)}
        </span>
      )}
    </div>
  );
}

/** Anel de progresso SVG (mesma receita do preparo em `VacuumOffer`). */
function ProgressRing({
  size,
  strokeWidth,
  fraction,
  color,
  track,
  children
}: {
  size: number;
  strokeWidth: number;
  fraction: number;
  color: string;
  track: string;
  children: React.ReactNode;
}) {
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(1, fraction));
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={track}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - clamped)}
          style={{ transition: 'stroke-dashoffset 1s linear' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children}
      </div>
    </div>
  );
}

/** Badge circular `$` da carteira Cashback (identidade Bow Carteiras). */
function CashbackBadge({ size = 24 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="flex flex-shrink-0 items-center justify-center rounded-full text-white"
      style={{
        width: size,
        height: size,
        background: 'linear-gradient(135deg,#835DD0,#C04CAE)',
        fontFamily: 'Chillax, system-ui, sans-serif',
        fontWeight: 700,
        fontSize: size * 0.5,
        lineHeight: 1
      }}
    >
      $
    </span>
  );
}

function WalletIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
      <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
      <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
    </svg>
  );
}

function ExitButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-4 inline-flex h-12 items-center justify-center rounded-full bg-bow-cream/15 px-8 text-sm font-semibold text-bow-cream backdrop-blur transition hover:bg-bow-cream/25"
    >
      {label}
    </button>
  );
}
